import { useEffect, useRef } from 'react';

/// A phone is narrower than the sheet the record was composed on. Reflowing it to fit would
/// change the document the patient came to read, so the narrow layout scales it instead: the
/// record keeps the width it was written at and is drawn smaller, the way a page of a pdf is.
///
/// Scaling only. The page is the one scroll surface - a window of our own inside it would
/// scroll against it - and zoom is the browser's own pinch over the whole page, which is why
/// nothing here listens to a gesture.
///
/// The stage (the record at its own width) is scaled with a transform, which takes no part in
/// layout, so a sizer behind it carries the scaled box and gives the page its height.

// Must match the breakpoint the narrow record layout uses in medicalRecord.css: below it the
// stage has a width of its own and this hook scales it, above it the record lays itself out
// and the hook leaves it alone.
const NARROW = 'screen and (max-width: 767px)';

export const useRecordFit = () => {
    const frameRef = useRef(null);
    const sizerRef = useRef(null);
    const stageRef = useRef(null);

    useEffect(() => {
        const frame = frameRef.current;
        const sizer = sizerRef.current;
        const stage = stageRef.current;
        if (!frame || !sizer || !stage) {
            return;
        }

        const narrow = window.matchMedia(NARROW);

        /// The stage is a fixed width, but content in the record can run past it - and with
        /// nothing here scrolling, whatever is not scaled in is out of reach.
        const documentWidth = () => Math.max(stage.offsetWidth, stage.scrollWidth);

        const fit = () => {
            const width = documentWidth();

            // A width of zero on either side is a moment mid layout - a rotation, a tab that
            // was in the background. Scaling to it would write scale(0) and stay there:
            // nothing that follows changes an untransformed size, so nothing wakes us again.
            if (!width || !frame.clientWidth) {
                return;
            }

            const scale = Math.min(1, frame.clientWidth / width);
            const box = { width: `${width * scale}px`, height: `${stage.offsetHeight * scale}px` };
            if (sizer.style.width === box.width && sizer.style.height === box.height) {
                // Nothing moved. Worth checking: this runs from a resize observer, and writing
                // the same sizes back would only wake it again.
                return;
            }

            sizer.style.width = box.width;
            sizer.style.height = box.height;
            stage.style.transform = `scale(${scale})`;
        }

        const release = () => {
            // The wide layout sizes the record itself - leave nothing of ours behind.
            stage.removeAttribute('style');
            sizer.removeAttribute('style');
        }

        /// Which layout is on screen, and the record's own size, both change under us - a
        /// rotation, a window dragged wider, an image that finished decoding. The observer
        /// sees all three, where a media query listener only sees the first two.
        const sync = () => narrow.matches ? fit() : release();

        const observer = new ResizeObserver(sync);
        observer.observe(frame);
        observer.observe(stage);

        sync();
        narrow.addEventListener('change', sync);

        return () => {
            narrow.removeEventListener('change', sync);
            observer.disconnect();
        }
    }, []);

    return { frameRef, sizerRef, stageRef };
}
